import { Link } from "@tanstack/react-router"
import { Loader2, Menu, PanelLeftClose, PanelLeftOpen, X } from "lucide-react"

import {
  MANAGE_NAVIGATION_GROUP,
  PRIMARY_NAVIGATION_GROUPS,
  type NavigationGroup,
  type NavigationItem,
  type NavigationItemId,
} from "./app-navigation"
import { NovaMark } from "./nova-mark"
import { ThemeToggle } from "./theme-toggle"
import { UserMenu } from "./user-menu"
import { cn } from "../../lib/utils"
import { Modal } from "../ui/modal"
import { Tooltip } from "../ui/tooltip"

type SidebarSharedProps = {
  activeItem: NavigationItemId | null
  isSyncingOrders: boolean
  shortcutLabel: string
}

type DesktopSidebarProps = SidebarSharedProps & {
  isExpanded: boolean
  onToggle: () => void
}

export function DesktopSidebar({
  activeItem,
  isExpanded,
  isSyncingOrders,
  onToggle,
  shortcutLabel,
}: DesktopSidebarProps) {
  const toggleLabel = `${isExpanded ? "Collapse" : "Expand"} sidebar (${shortcutLabel})`

  return (
    <aside
      id="app-desktop-sidebar"
      className={cn(
        "fixed inset-y-0 left-0 z-40 hidden flex-col border-r border-border bg-background lg:flex",
        "transition-[width] duration-200 ease-[var(--ease-product-out)] motion-reduce:transition-none",
        isExpanded ? "w-60" : "w-16"
      )}
    >
      <div
        className={cn("flex h-14 shrink-0 items-center", isExpanded ? "px-3" : "justify-center")}
      >
        <Link
          to="/dashboard"
          aria-label="GymBeam Nova dashboard"
          className="flex min-w-0 items-center rounded-lg outline-none transition-opacity hover:opacity-75 focus-visible:ring-2 focus-visible:ring-ring"
        >
          {isExpanded ? (
            <span className="flex min-w-0 items-center gap-2.5">
              <img src="/gymbeam-logo.svg" alt="GymBeam" className="app-wordmark h-5 w-auto" />

              <span className="h-5 w-px bg-border" aria-hidden="true" />

              <NovaMark wordmark className="text-foreground" />
            </span>
          ) : (
            <NovaMark />
          )}
        </Link>

        {isExpanded ? <div className="flex-1" /> : null}

        <Tooltip content={toggleLabel} placement="right">
          <button
            type="button"
            onClick={onToggle}
            aria-label={toggleLabel}
            aria-expanded={isExpanded}
            aria-controls="app-desktop-sidebar"
            className={cn(
              "flex size-8 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
              !isExpanded && "absolute left-11 top-3 border border-border bg-card shadow-card"
            )}
          >
            {isExpanded ? (
              <PanelLeftClose className="size-4" />
            ) : (
              <PanelLeftOpen className="size-4" />
            )}
          </button>
        </Tooltip>
      </div>

      <SidebarContent
        activeItem={activeItem}
        isExpanded={isExpanded}
        isSyncingOrders={isSyncingOrders}
      />
    </aside>
  )
}

type MobileAppHeaderProps = {
  isOpen: boolean
  onOpen: () => void
  shortcutLabel: string
}

export function MobileAppHeader({ isOpen, onOpen, shortcutLabel }: MobileAppHeaderProps) {
  const label = `Open navigation (${shortcutLabel})`
  return (
    <header className="sticky top-0 z-40 flex h-14 items-center border-b border-border/80 bg-background/95 px-4 backdrop-blur-sm lg:hidden">
      <Link
        to="/dashboard"
        aria-label="GymBeam Nova dashboard"
        className="rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <NovaMark wordmark className="text-foreground" />
      </Link>

      <button
        type="button"
        onClick={onOpen}
        aria-label={label}
        aria-expanded={isOpen}
        aria-controls="app-mobile-sidebar"
        className="ml-auto inline-flex h-9 items-center gap-2 rounded-full border border-border bg-card px-3 text-[13px] font-medium text-foreground shadow-card outline-none transition-colors hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Menu className="size-4" />
        Menu
      </button>
    </header>
  )
}

type MobileSidebarDrawerProps = SidebarSharedProps & {
  isOpen: boolean
  onClose: () => void
}

export function MobileSidebarDrawer({
  activeItem,
  isOpen,
  isSyncingOrders,
  onClose,
}: MobileSidebarDrawerProps) {
  if (!isOpen) {
    return null
  }

  return (
    <div className="lg:hidden">
      <Modal
        label="Navigation"
        onClose={onClose}
        backdropClassName="motion-app-sidebar-backdrop"
        surfaceClassName="motion-app-sidebar-drawer absolute inset-y-0 left-0 max-h-none max-w-[20rem] rounded-none border-y-0 border-l-0"
      >
        <div id="app-mobile-sidebar" className="flex min-h-0 flex-1 flex-col bg-background">
          <div className="flex h-14 shrink-0 items-center gap-3 px-4">
            <NovaMark wordmark className="text-foreground" />

            <button
              type="button"
              onClick={onClose}
              aria-label="Close navigation"
              className="ml-auto flex size-8 items-center justify-center rounded-full text-muted-foreground outline-none transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </button>
          </div>

          <SidebarContent
            activeItem={activeItem}
            isExpanded
            isSyncingOrders={isSyncingOrders}
            onNavigate={onClose}
          />
        </div>
      </Modal>
    </div>
  )
}

type SidebarContentProps = {
  activeItem: NavigationItemId | null
  isExpanded: boolean
  isSyncingOrders: boolean
  onNavigate?: () => void
}

function SidebarContent({
  activeItem,
  isExpanded,
  isSyncingOrders,
  onNavigate,
}: SidebarContentProps) {
  return (
    <>
      <nav aria-label="Global navigation" className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
        {PRIMARY_NAVIGATION_GROUPS.map((group, index) => (
          <NavigationGroupView
            key={group.label ?? "dashboard"}
            group={group}
            activeItem={activeItem}
            isExpanded={isExpanded}
            isSyncingOrders={isSyncingOrders}
            isFirst={index === 0}
            {...(onNavigate ? { onNavigate } : {})}
          />
        ))}
      </nav>

      <div className="shrink-0 border-t border-border px-2 py-2">
        <NavigationGroupView
          group={MANAGE_NAVIGATION_GROUP}
          activeItem={activeItem}
          isExpanded={isExpanded}
          isSyncingOrders={isSyncingOrders}
          isFirst
          {...(onNavigate ? { onNavigate } : {})}
        />

        <div className={cn("mt-2 flex flex-col gap-2", !isExpanded && "items-center")}>
          {isExpanded ? (
            <ThemeToggle showLabel />
          ) : (
            <Tooltip content="Change theme" placement="right">
              <ThemeToggle />
            </Tooltip>
          )}

          {isExpanded ? (
            <UserMenu />
          ) : (
            <Tooltip content="Account menu" placement="right">
              <UserMenu compact />
            </Tooltip>
          )}
        </div>
      </div>
    </>
  )
}

type NavigationGroupViewProps = SidebarContentProps & {
  group: NavigationGroup
  isFirst: boolean
}

function NavigationGroupView({
  activeItem,
  group,
  isExpanded,
  isFirst,
  isSyncingOrders,
  onNavigate,
}: NavigationGroupViewProps) {
  return (
    <section className={cn(!isFirst && (isExpanded ? "mt-5" : "mt-3 border-t border-border pt-3"))}>
      {isExpanded && group.label ? (
        <h2 className="mb-1 px-2 text-xs font-medium text-muted-foreground">{group.label}</h2>
      ) : null}

      <div className="space-y-1">
        {group.items.map((item) => (
          <NavigationLink
            key={item.id}
            item={item}
            isActive={activeItem === item.id}
            isExpanded={isExpanded}
            isSyncingOrders={isSyncingOrders}
            {...(onNavigate ? { onNavigate } : {})}
          />
        ))}
      </div>
    </section>
  )
}

type NavigationLinkProps = {
  item: NavigationItem
  isActive: boolean
  isExpanded: boolean
  isSyncingOrders: boolean
  onNavigate?: () => void
}

function NavigationLink({
  item,
  isActive,
  isExpanded,
  isSyncingOrders,
  onNavigate,
}: NavigationLinkProps) {
  const Icon = item.icon
  const showsSync = item.id === "orders" && isSyncingOrders
  const accessibleLabel = showsSync ? `${item.label}, syncing orders` : item.label
  const link = (
    <Link
      to={item.to}
      aria-label={accessibleLabel}
      {...(isActive ? { "aria-current": "page" as const } : {})}
      {...(onNavigate ? { onClick: onNavigate } : {})}
      className={cn(
        "flex h-9 w-full items-center rounded-lg text-[13px] font-medium outline-none transition-colors",
        "focus-visible:ring-2 focus-visible:ring-ring",
        isExpanded ? "gap-3 px-2.5" : "justify-center",
        isActive
          ? "bg-card text-foreground shadow-card ring-1 ring-border"
          : "text-muted-foreground hover:bg-foreground/5 hover:text-foreground"
      )}
    >
      <span className="relative flex size-4 shrink-0 items-center justify-center">
        <Icon className="size-4" />

        {showsSync && !isExpanded ? (
          <Loader2
            role="status"
            aria-label="Syncing orders"
            className="absolute -bottom-1.5 -right-1.5 size-3 animate-spin rounded-full bg-background text-info"
          />
        ) : null}
      </span>

      {isExpanded ? <span className="truncate">{item.label}</span> : null}

      {showsSync && isExpanded ? (
        <span className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-info">
          <Loader2 className="size-3 animate-spin" />
          Syncing orders…
        </span>
      ) : null}
    </Link>
  )

  if (isExpanded) {
    return link
  }
  return (
    <Tooltip
      content={showsSync ? `${item.label} — Syncing orders…` : item.label}
      placement="right"
      className="w-full"
    >
      {link}
    </Tooltip>
  )
}
